import React, { useState, useRef, useCallback, useEffect } from "react";
import { speakText } from "../utils/speech";
import HanziWriter from "hanzi-writer";
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CreateIcon from '@mui/icons-material/Create';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import BrushIcon from '@mui/icons-material/Brush';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

export default function ChineseFlashCardView({ words, onExit }) {
    const [flashIndex, setFlashIndex] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [practiceMode, setPracticeMode] = useState(false);
    const [practiceType, setPracticeType] = useState(null);
    const [practiceAttempts, setPracticeAttempts] = useState([]);
    const [currentInput, setCurrentInput] = useState('');
    const [strokeComplete, setStrokeComplete] = useState(false);
    const [currentCharIndex, setCurrentCharIndex] = useState(0);
    const [strokeKey, setStrokeKey] = useState(0);
    const practiceInputRef = useRef(null);
    const strokeContainerRef = useRef(null);
    const writerRef = useRef(null);
    const attemptsRef = useRef(0);

    const currentWord = words[flashIndex];
    const currentChars = currentWord?.chinese?.split('') || [];

    const speakWord = useCallback((index = flashIndex) => {
        if (!words[index]) return;
        speakText(words[index].chinese, 'zh', 0.8);
    }, [words, flashIndex]);


    useEffect(() => { speakWord(); }, [flashIndex, speakWord]);

    const startPractice = (type) => {
        setPracticeMode(true);
        setPracticeType(type);
        setPracticeAttempts([]);
        setCurrentInput('');
        setStrokeComplete(false);
        setCurrentCharIndex(0);
        setStrokeKey(0);
        attemptsRef.current = 0;
        if (type === 'pinyin') setTimeout(() => practiceInputRef.current?.focus(), 100);
    };

    const normalizePinyin = (p) => p.toLowerCase()
        .replace(/[āáǎà]/g, 'a').replace(/[ēéěè]/g, 'e')
        .replace(/[īíǐì]/g, 'i').replace(/[ōóǒò]/g, 'o')
        .replace(/[ūúǔù]/g, 'u').replace(/[ǖǘǚǜü]/g, 'v')
        .replace(/\s+/g, '').trim();

    const handlePracticeSubmit = () => {
        if (!currentInput.trim()) return;
        const correct = normalizePinyin(currentWord.pinyin);
        const user = normalizePinyin(currentInput);
        const isCorrect = user === correct;
        const newAttempts = [...practiceAttempts, { input: currentInput, correct: isCorrect }];
        setPracticeAttempts(newAttempts);
        setCurrentInput('');
        if (newAttempts.length >= 10) {
            setTimeout(() => {
                const n = newAttempts.filter(a => a.correct).length;
                alert(n === 10
                    ? `Perfect! You typed "${currentWord.pinyin}" correctly 10 times!`
                    : `Good effort! You got ${n}/10 correct. Keep practicing!`
                );
                setPracticeMode(false);
                setPracticeAttempts([]);
                setPracticeType(null);
            }, 100);
        } else {
            practiceInputRef.current?.focus();
        }
    };

    const initStrokeWriter = useCallback(() => {
        if (!strokeContainerRef.current) return;
        const chars = currentWord?.chinese?.split('') || [];
        if (!chars[currentCharIndex]) return;
        if (writerRef.current) writerRef.current = null;
        strokeContainerRef.current.innerHTML = '';
        try {
            writerRef.current = HanziWriter.create(strokeContainerRef.current, chars[currentCharIndex], {
                width: 240, height: 240, padding: 10,
                showOutline: true, showCharacter: false,
                strokeColor: '#4f46e5', outlineColor: '#e0e7ff',
                highlightColor: '#a5b4fc', radicalColor: '#6366f1',
                highlightOnComplete: true, highlightCompleteColor: '#22c55e',
                drawingWidth: 20, showHintAfterMisses: 3,
                acceptBackwardsStrokes: true, leniency: 1.5,
            });
            writerRef.current.quiz({
                onComplete: () => {
                    if (currentCharIndex < chars.length - 1) {
                        setTimeout(() => setCurrentCharIndex(prev => prev + 1), 800);
                    } else {
                        setStrokeComplete(true);
                        attemptsRef.current += 1;
                        setPracticeAttempts(prev => [...prev, { correct: true }]);
                        if (attemptsRef.current >= 3) {
                            setTimeout(() => {
                                alert(`Great! You practiced "${currentWord.chinese}" 3 times!`);
                                setPracticeMode(false);
                                setPracticeAttempts([]);
                                setPracticeType(null);
                                attemptsRef.current = 0;
                            }, 500);
                        }
                    }
                }
            });
        } catch (err) { console.error('HanziWriter error:', err); }
    }, [currentCharIndex, currentWord]);

    useEffect(() => {
        let timer;
        if (practiceMode && practiceType === 'stroke') {
            timer = setTimeout(() => initStrokeWriter(), 300);
        }
        return () => {
            if (timer) clearTimeout(timer);
            if (writerRef.current) writerRef.current = null;
        };
    }, [practiceMode, practiceType, currentCharIndex, strokeKey, initStrokeWriter]);

    const exitPractice = () => {
        setPracticeMode(false);
        setPracticeAttempts([]);
        setCurrentInput('');
        setPracticeType(null);
        setStrokeComplete(false);
        setCurrentCharIndex(0);
        setStrokeKey(0);
        attemptsRef.current = 0;
    };

    const hasTranslation = !!(currentWord?.pinyin || currentWord?.english);

    return (
        <div className="fc-view">
            {/* Header */}
            <div className="fc-header">
                <span className="fc-progress">{flashIndex + 1} / {words.length}</span>
                <button className="fc-exit-btn" onClick={onExit}>
                    <CloseOutlinedIcon style={{ fontSize: 20 }} />
                </button>
            </div>

            {!practiceMode ? (
                <>
                    {/* Card */}
                    <div
                        className={`fc-card ${flipped ? 'fc-card-back' : 'fc-card-front'}`}
                        onClick={() => hasTranslation && setFlipped(!flipped)}
                        style={{ cursor: hasTranslation ? 'pointer' : 'default' }}
                    >
                        {!flipped ? (
                            <div className="fc-card-inner">
                                {hasTranslation && <p className="fc-card-label">Tap to reveal meaning</p>}
                                <div className="fc-word fc-chinese-char">{currentWord?.chinese}</div>
                                {hasTranslation && <SyncAltIcon className="fc-flip-icon" />}
                            </div>
                        ) : (
                            <div className="fc-card-inner">
                                <p className="fc-card-label">Tap to see character</p>
                                {currentWord?.pinyin && <div className="fc-pinyin">{currentWord.pinyin}</div>}
                                {currentWord?.english && <div className="fc-meaning">{currentWord.english}</div>}
                                <SyncAltIcon className="fc-flip-icon" />
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="fc-actions">
                        <button className="fc-btn fc-btn-audio" onClick={() => speakWord(flashIndex)}>
                            <VolumeUpIcon />
                            <span>Play Sound</span>
                        </button>
                    </div>

                    {/* Practice buttons */}
                    <div className="fc-practice-options">
                        {hasTranslation && (
                            <button className="fc-btn fc-btn-practice" onClick={() => startPractice('pinyin')}>
                                <CreateIcon />
                                <span>Practice Pinyin</span>
                            </button>
                        )}
                        <button className="fc-btn fc-btn-stroke" onClick={() => startPractice('stroke')}>
                            <BrushIcon />
                            <span>Practice Stroke</span>
                        </button>
                    </div>

                    {/* Navigation */}
                    <div className="fc-nav">
                        <button
                            className="fc-nav-btn"
                            onClick={() => { setFlashIndex(flashIndex - 1); setFlipped(false); }}
                            disabled={flashIndex === 0}
                        >
                            <ArrowBackIcon />
                        </button>
                        <div className="fc-nav-dots">
                            {words.slice(Math.max(0, flashIndex - 2), Math.min(words.length, flashIndex + 3)).map((_, i) => {
                                const idx = Math.max(0, flashIndex - 2) + i;
                                return <span key={idx} className={`fc-dot${idx === flashIndex ? ' active' : ''}`} />;
                            })}
                        </div>
                        {flashIndex + 1 < words.length ? (
                            <button
                                className="fc-nav-btn fc-nav-next"
                                onClick={() => { setFlashIndex(flashIndex + 1); setFlipped(false); }}
                            >
                                <ArrowForwardIcon />
                            </button>
                        ) : (
                            <button className="fc-nav-btn fc-nav-next fc-nav-finish" onClick={onExit}>
                                <CheckIcon />
                            </button>
                        )}
                    </div>
                </>
            ) : (
                <div className="fc-practice">
                    {practiceType === 'pinyin' ? (
                        <>
                            <div className="fc-practice-header">
                                <span className="fc-practice-word fc-chinese-char-sm">{currentWord?.chinese}</span>
                                <span className="fc-practice-target">{currentWord?.pinyin}</span>
                                <div className="fc-practice-prog-label">{practiceAttempts.length} / 10</div>
                            </div>
                            <div className="fc-practice-bar">
                                <div className="fc-practice-bar-fill" style={{ width: `${(practiceAttempts.length / 10) * 100}%` }} />
                            </div>
                            <div className="fc-practice-input-row">
                                <input
                                    ref={practiceInputRef}
                                    type="text"
                                    className="fc-practice-input"
                                    value={currentInput}
                                    onChange={(e) => setCurrentInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePracticeSubmit(); } }}
                                    placeholder="Type the pinyin..."
                                    autoComplete="off"
                                />
                                <button className="fc-practice-submit" onClick={handlePracticeSubmit} disabled={!currentInput.trim()}>
                                    <CheckIcon />
                                </button>
                            </div>
                            <div className="fc-attempts">
                                {practiceAttempts.length === 0 ? (
                                    <p className="fc-attempts-empty">No attempts yet — start typing!</p>
                                ) : (
                                    <div className="fc-attempts-list">
                                        {practiceAttempts.map((a, i) => (
                                            <div key={i} className={`fc-attempt ${a.correct ? 'correct' : 'incorrect'}`}>
                                                <span>{i + 1}. {a.input}</span>
                                                {a.correct ? <CheckIcon /> : <CloseIcon />}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="fc-practice-header">
                                <span className="fc-practice-word fc-chinese-char-sm">{currentWord?.chinese}</span>
                                <div className="fc-practice-prog-label">{practiceAttempts.length} / 3</div>
                            </div>
                            <div className="fc-practice-bar">
                                <div className="fc-practice-bar-fill" style={{ width: `${(practiceAttempts.length / 3) * 100}%` }} />
                            </div>

                            {currentChars.length > 1 && (
                                <div className="fc-stroke-char-row">
                                    {currentChars.map((char, idx) => (
                                        <span key={idx} className={`fc-stroke-char-dot${idx < currentCharIndex ? ' done' : ''}${idx === currentCharIndex ? ' current' : ''}`}>
                                            {idx <= currentCharIndex ? char : '○'}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="fc-stroke-canvas-wrap">
                                <div ref={strokeContainerRef} className="stroke-canvas" />
                                {strokeComplete && (
                                    <div className="stroke-complete-overlay">
                                        <CheckIcon className="icon-xl" />
                                    </div>
                                )}
                            </div>

                            <div className="fc-stroke-actions">
                                <button className="fc-btn fc-btn-outline" onClick={() => initStrokeWriter()}>
                                    <RefreshIcon /> Reset
                                </button>
                                {strokeComplete && (
                                    <button className="fc-btn fc-btn-success" onClick={() => { setStrokeComplete(false); setCurrentCharIndex(0); setStrokeKey(prev => prev + 1); }}>
                                        <ArrowForwardIcon /> Next
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    <button className="btn btn-outline" onClick={exitPractice} style={{ width: '100%', marginTop: 12 }}>
                        <ArrowBackIcon className="icon-sm" /> Back to Card
                    </button>
                </div>
            )}
        </div>
    );
}
